// ============================================
// Presence Indicator Component
// Colored dot showing user's online status
// ============================================

import React from 'react';

/**
 * Status dot indicator for user presence
 * @param {string} status - 'online', 'away', 'dnd', 'offline'
 * @param {string} size - 'sm', 'md', 'lg'
 * @param {boolean} showBorder - Whether to show white border (for overlays)
 */
const PresenceIndicator = ({ status = 'offline', size = 'sm', showBorder = true }) => {
  // Size classes
  const sizeClasses = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-3.5 h-3.5'
  };

  // Status colors
  const statusColors = {
    online: 'bg-green-500',
    away: 'bg-yellow-500',
    dnd: 'bg-red-500',
    offline: 'bg-gray-400'
  };

  // Status titles for accessibility
  const statusTitles = {
    online: 'Online',
    away: 'Away',
    dnd: 'Do not disturb',
    offline: 'Offline'
  };

  return (
    <span
      className={`
        inline-block rounded-full
        ${sizeClasses[size] || sizeClasses.sm}
        ${statusColors[status] || statusColors.offline}
        ${showBorder ? 'ring-2 ring-white' : ''}
      `}
      title={statusTitles[status] || 'Unknown'}
      aria-label={statusTitles[status] || 'Unknown status'}
    />
  );
};

/**
 * Avatar with presence indicator overlay
 */
export const AvatarWithPresence = ({
  name,
  avatarUrl,
  userType,
  status = 'offline',
  size = 'md',
  onClick,
  className = ''
}) => {
  // Avatar size classes
  const avatarSizes = {
    sm: 'w-6 h-6 text-[10px]',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm'
  };

  // Presence indicator position
  const indicatorPositions = {
    sm: '-bottom-0.5 -right-0.5',
    md: '-bottom-0.5 -right-0.5',
    lg: '-bottom-1 -right-1'
  };

  // Get avatar color based on user type
  const getAvatarColor = (type) => {
    switch (type) {
      case 'student': return 'bg-blue-500';
      case 'recruiter': return 'bg-green-500';
      case 'alumni': return 'bg-purple-500';
      case 'admin': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const Wrapper = onClick ? 'button' : 'div';
  const wrapperProps = onClick ? {
    onClick,
    className: `relative inline-block focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full ${className}`
  } : {
    className: `relative inline-block ${className}`
  };

  return (
    <Wrapper {...wrapperProps}>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name || 'User'}
          className={`rounded-full object-cover ${avatarSizes[size] || avatarSizes.md}`}
        />
      ) : (
        <div className={`rounded-full ${getAvatarColor(userType)} flex items-center justify-center text-white font-medium ${avatarSizes[size] || avatarSizes.md}`}>
          {getInitials(name)}
        </div>
      )}

      {/* Presence indicator */}
      <span className={`absolute ${indicatorPositions[size] || indicatorPositions.md}`}>
        <PresenceIndicator status={status} size="sm" showBorder />
      </span>
    </Wrapper>
  );
};

export default PresenceIndicator;
